import { Controller } from "@hotwired/stimulus";

/**
 * Hero Mesh Gradient Controller
 *
 * Creates a static mesh gradient background for the hero section using the Paper Design shader library.
 * Uses the static shader (no animation) for reduced GPU load.
 * Supports dark mode with automatic color switching.
 */
export default class extends Controller<HTMLElement> {
  private shaderMount: any = null;
  private themeObserver: MutationObserver | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private systemThemeListener: ((e: MediaQueryListEvent) => void) | null = null;
  private getShaderColorFromString: ((color: string) => number[]) | null = null;

  // Light mode colors - balanced for readability with visible green accent
  private lightColors = [
    "rgba(215, 212, 200, 0.5)", // Light beige
    "rgba(200, 195, 185, 0.5)", // Silver
    "rgba(140, 185, 120, 0.6)", // Green
    "rgba(220, 218, 210, 0.4)", // Extra beige for 4-color static gradient
  ];

  // Dark mode colors - toned down green for better contrast
  private darkColors = [
    "rgba(61, 58, 53, 0.5)", // Dark beige (base-200, more transparent)
    "rgba(74, 71, 65, 0.75)", // Dark silver
    "rgba(160, 200, 130, 0.6)", // Muted green (reduced brightness and opacity)
    "rgba(55, 52, 48, 0.4)", // Extra dark beige for 4-color static gradient
  ];

  // Static shader settings - tuned to approximate the animated look
  private lightSettings = {
    u_positions: 45,
    u_waveX: 0.35,
    u_waveXShift: 0.3,
    u_waveY: 0.4,
    u_waveYShift: 0.5,
    u_mixing: 0.55,
    u_scale: 1.12,
    u_rotation: 64,
    u_offsetX: 0,
    u_offsetY: 0,
  };

  private darkSettings = {
    u_positions: 45,
    u_waveX: 0.35,
    u_waveXShift: 0.3,
    u_waveY: 0.4,
    u_waveYShift: 0.5,
    u_mixing: 0.6,
    u_scale: 1.12,
    u_rotation: 64,
    u_offsetX: 0,
    u_offsetY: 0,
  };

  async connect(): Promise<void> {
    await this.initShader();
    this.setupThemeObserver();
    this.setupSystemThemeListener();
  }

  disconnect(): void {
    if (this.shaderMount?.dispose) {
      this.shaderMount.dispose();
      this.shaderMount = null;
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
    this.removeSystemThemeListener();
  }

  private isDarkMode(): boolean {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }
    const savedTheme = localStorage.getItem("lumenTheme");
    if (savedTheme === "dark") return true;
    if (savedTheme === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getColors(): string[] {
    return this.isDarkMode() ? this.darkColors : this.lightColors;
  }

  private getSettings(): typeof this.lightSettings {
    return this.isDarkMode() ? this.darkSettings : this.lightSettings;
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          this.updateColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  private setupSystemThemeListener(): void {
    if (!window.matchMedia) return;

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemThemeListener = () => {
      const savedTheme = localStorage.getItem("lumenTheme");
      if (!savedTheme || savedTheme === "system") {
        this.updateColors();
      }
    };

    this.mediaQuery.addEventListener("change", this.systemThemeListener);
  }

  private removeSystemThemeListener(): void {
    if (this.mediaQuery && this.systemThemeListener) {
      this.mediaQuery.removeEventListener("change", this.systemThemeListener);
    }
  }

  private updateColors(): void {
    if (!this.shaderMount || !this.getShaderColorFromString) return;

    const colorStrings = this.getColors();
    const colors = colorStrings.map((c) => this.getShaderColorFromString!(c));
    const settings = this.getSettings();

    this.shaderMount.setUniforms({
      u_colors: colors,
      u_colorsCount: colors.length,
      ...settings,
    });
  }

  private async initShader(): Promise<void> {
    try {
      const {
        ShaderMount,
        staticMeshGradientFragmentShader,
        getShaderColorFromString,
      } = await import("@paper-design/shaders");

      this.getShaderColorFromString = getShaderColorFromString;

      const colorStrings = this.getColors();
      const colors = colorStrings.map((c) => getShaderColorFromString(c));
      const settings = this.getSettings();

      this.shaderMount = new ShaderMount(
        this.element,
        staticMeshGradientFragmentShader,
        {
          u_colors: colors,
          u_colorsCount: colors.length,
          u_grainMixer: 0.25,
          u_grainOverlay: 0.05,
          ...settings,
        },
        { alpha: true, premultipliedAlpha: true },
        0 // speed = 0 for static
      );
    } catch (error) {
      console.error("Failed to initialize hero mesh gradient shader:", error);
    }
  }
}
